import React, { createContext, useContext, useState, useCallback, useEffect } from 'react'
import { Tenant, ManagedInvestment, SalesUnit, UnitType, UnitStatus, WidgetFieldConfig, WidgetFieldKey } from '../types/management'
import { supabase } from '../supabaseClient'
import { getInvestmentPropertiesAtDate } from '@repo/supabase'

// Helper: Transformacja tablicy stringów z bazy na WidgetFieldConfig[]
// Baza przechowuje: ["area", "rooms", "floor"] → transformujemy na: [{field: "area", order: 1, enabled: true}, ...]
function parseWidgetFieldsFromDb(dbFields: string[] | null | undefined): WidgetFieldConfig[] | undefined {
  if (!dbFields || !Array.isArray(dbFields)) return undefined
  return dbFields.map((field, index) => ({
    field: field as WidgetFieldKey,
    order: index + 1,
    enabled: true
  }))
}

// Types for the context
interface ManagementContextValue {
  // Data
  tenants: Tenant[]
  investments: ManagedInvestment[]
  units: SalesUnit[]

  // Selected state
  selectedTenant: Tenant | null
  selectedInvestment: ManagedInvestment | null

  // Loading states
  isLoadingTenants: boolean
  isLoadingInvestments: boolean
  isLoadingUnits: boolean

  // Error states
  error: string | null

  // Actions
  selectTenant: (tenantId: string | null) => void
  selectInvestment: (investmentId: string | null) => void
  refreshTenants: () => Promise<void>
  refreshInvestments: () => Promise<void>
  refreshUnits: () => Promise<void>
}

const ManagementContext = createContext<ManagementContextValue | null>(null)

export const useManagement = () => {
  const context = useContext(ManagementContext)
  if (!context) {
    throw new Error('useManagement must be used within ManagementProvider')
  }
  return context
}

interface ManagementProviderProps {
  children: React.ReactNode
}

export const ManagementProvider: React.FC<ManagementProviderProps> = ({ children }) => {
  // Data state
  const [tenants, setTenants] = useState<Tenant[]>([])
  const [investments, setInvestments] = useState<ManagedInvestment[]>([])
  const [units, setUnits] = useState<SalesUnit[]>([])

  // Selected state
  const [selectedTenantId, setSelectedTenantId] = useState<string | null>(null)
  const [selectedInvestmentId, setSelectedInvestmentId] = useState<string | null>(null)

  // Loading states
  const [isLoadingTenants, setIsLoadingTenants] = useState(false)
  const [isLoadingInvestments, setIsLoadingInvestments] = useState(false)
  const [isLoadingUnits, setIsLoadingUnits] = useState(false)

  // Error state
  const [error, setError] = useState<string | null>(null)

  // Derived state
  const selectedTenant = tenants.find(t => t.id === selectedTenantId) || null
  const selectedInvestment = investments.find(i => i.id === selectedInvestmentId) || null

  // Fetch tenants for current user (via user_tenant_permissions)
  const refreshTenants = useCallback(async () => {
    setIsLoadingTenants(true)
    setError(null)

    try {
      const { data: { user } } = await supabase.auth.getUser()

      if (!user) {
        setError('Nie jestes zalogowany')
        setTenants([])
        return
      }

      // Get tenants user has access to
      const { data, error: fetchError } = await supabase
        .from('tenants')
        .select(`
          id,
          investor_name,
          slug,
          legal_form,
          investor_nip,
          investor_krs,
          regon,
          investor_email,
          investor_phone,
          investor_website,
          deployment_email,
          hq_street,
          hq_building_number,
          hq_apartment_number,
          hq_city,
          hq_postal_code,
          hq_voivodeship,
          hq_county,
          hq_commune,
          created_at,
          user_tenant_permissions!inner(user_id)
        `)
        .eq('user_tenant_permissions.user_id', user.id)
        .order('created_at', { ascending: false })

      if (fetchError) {
        console.error('Error fetching tenants:', fetchError)
        setError('Blad pobierania listy deweloperow')
        return
      }

      // Map Supabase data to our Tenant type
      // investor_name -> name, investor_email -> contact.email, etc.
      const mappedTenants: Tenant[] = (data || []).map(row => ({
        id: row.id,
        name: row.investor_name || '',
        slug: row.slug,
        legalForm: row.legal_form || undefined,
        nip: row.investor_nip || undefined,
        krs: row.investor_krs || undefined,
        regon: row.regon || undefined,
        address: {
          street: row.hq_street || '',
          buildingNumber: row.hq_building_number || '',
          flatNumber: row.hq_apartment_number || undefined,
          city: row.hq_city || '',
          zipCode: row.hq_postal_code || '',
          voivodeship: row.hq_voivodeship || undefined,
          district: row.hq_county || undefined,
          community: row.hq_commune || undefined
        },
        contact: {
          email: row.investor_email || '',
          phone: row.investor_phone || '',
          website: row.investor_website || undefined
        },
        deploymentEmail: row.deployment_email || undefined,
        createdAt: row.created_at
      }))

      setTenants(mappedTenants)
    } catch (err) {
      console.error('Error in refreshTenants:', err)
      setError('Nieoczekiwany blad')
    } finally {
      setIsLoadingTenants(false)
    }
  }, [])

  // Fetch investments for selected tenant
  const refreshInvestments = useCallback(async () => {
    if (!selectedTenantId) {
      setInvestments([])
      return
    }

    setIsLoadingInvestments(true)
    setError(null)

    try {
      const { data, error: fetchError } = await supabase
        .from('investments')
        .select(`
          id,
          tenant_id,
          name,
          slug,
          description,
          location_street,
          location_building_number,
          location_city,
          location_postal_code,
          location_voivodeship,
          location_county,
          location_commune,
          sales_street,
          sales_building_number,
          sales_apartment_number,
          sales_city,
          sales_postal_code,
          sales_voivodeship,
          sales_county,
          sales_commune,
          contact_url,
          module_gov_reporting,
          module_developer_widget,
          widget_property_details_list,
          widget_property_details_card,
          created_at,
          stages(id, name)
        `)
        .eq('tenant_id', selectedTenantId)

      if (fetchError) {
        console.error('Error fetching investments:', fetchError)
        setError('Blad pobierania inwestycji')
        return
      }

      // Map to ManagedInvestment type
      const mappedInvestments: ManagedInvestment[] = (data || []).map(row => ({
        id: row.id,
        tenantId: row.tenant_id,
        name: row.name,
        slug: row.slug,
        status: 'ACTIVE' as const, // TODO: Add status field to investments table
        stages: (row.stages || []).map((s: any) => ({ id: s.id, name: s.name })),
        investmentAddress: {
          street: row.location_street || '',
          buildingNumber: row.location_building_number || '',
          city: row.location_city || '',
          zipCode: row.location_postal_code || '',
          voivodeship: row.location_voivodeship || undefined,
          district: row.location_county || undefined,
          community: row.location_commune || undefined
        },
        salesOfficeAddress: {
          street: row.sales_street || '',
          buildingNumber: row.sales_building_number || '',
          flatNumber: row.sales_apartment_number || undefined,
          city: row.sales_city || '',
          zipCode: row.sales_postal_code || '',
          voivodeship: row.sales_voivodeship || undefined,
          district: row.sales_county || undefined,
          community: row.sales_commune || undefined
        },
        contact: {
          website: row.contact_url || undefined
        },
        modules: {
          govReporting: row.module_gov_reporting ?? true,
          developerWidget: row.module_developer_widget ?? false
        },
        widgetListFields: parseWidgetFieldsFromDb(row.widget_property_details_list),
        widgetCardFields: parseWidgetFieldsFromDb(row.widget_property_details_card)
      }))

      setInvestments(mappedInvestments)
    } catch (err) {
      console.error('Error in refreshInvestments:', err)
      setError('Nieoczekiwany blad')
    } finally {
      setIsLoadingInvestments(false)
    }
  }, [selectedTenantId])

  // Fetch units (properties) for selected investment using RPC
  // RPC get_investment_properties_at_date pobiera aktualną cenę i status z tabel wersjonujących
  const refreshUnits = useCallback(async () => {
    if (!selectedInvestmentId) {
      setUnits([])
      return
    }

    setIsLoadingUnits(true)
    setError(null)

    try {
      // Get investment slug for RPC call
      const investment = investments.find(i => i.id === selectedInvestmentId)
      if (!investment) {
        setUnits([])
        return
      }

      // Use RPC to get properties with current prices/status from versioning tables
      const data = await getInvestmentPropertiesAtDate(
        supabase,
        investment.slug,
        new Date()
      )

      // Map to SalesUnit type - RPC returns total_price from property_price_versions
      const mappedUnits: SalesUnit[] = (data || []).map((row: any) => ({
        id: row.id,
        investmentId: selectedInvestmentId,
        stageId: row.stage_id,
        type: asUnitType(row.property_type),
        status: asUnitStatus(row.status_code),
        number: row.external_id || row.title || '',
        area: Number(row.area_total) || 0,
        basePrice: Number(row.total_price) || 0, // RPC returns total_price from versions table
        additionalComponents: [],
        history: []
      }))

      setUnits(mappedUnits)
    } catch (err) {
      console.error('Error in refreshUnits:', err)
      setError('Nieoczekiwany blad')
    } finally {
      setIsLoadingUnits(false)
    }
  }, [selectedInvestmentId, investments])

  // Selection handlers
  const selectTenant = useCallback((tenantId: string | null) => {
    setSelectedTenantId(tenantId)
    setSelectedInvestmentId(null)
    setInvestments([])
    setUnits([])
  }, [])

  const selectInvestment = useCallback((investmentId: string | null) => {
    setSelectedInvestmentId(investmentId)
    setUnits([])
  }, [])

  // Load tenants on mount
  useEffect(() => {
    refreshTenants()
  }, [refreshTenants])

  // Load investments when tenant changes
  useEffect(() => {
    if (selectedTenantId) {
      refreshInvestments()
    }
  }, [selectedTenantId, refreshInvestments])

  // Load units when investment changes
  useEffect(() => {
    if (selectedInvestmentId) {
      refreshUnits()
    }
  }, [selectedInvestmentId, refreshUnits])

  const value: ManagementContextValue = {
    tenants,
    investments,
    units,
    selectedTenant,
    selectedInvestment,
    isLoadingTenants,
    isLoadingInvestments,
    isLoadingUnits,
    error,
    selectTenant,
    selectInvestment,
    refreshTenants,
    refreshInvestments,
    refreshUnits
  }

  return (
    <ManagementContext.Provider value={value}>
      {children}
    </ManagementContext.Provider>
  )
}

// Typy są zgodne z bazą danych - bez mapowania
function asUnitType(type: string | null): UnitType {
  return (type as UnitType) || 'lokal-mieszkalny'
}

function asUnitStatus(status: string | null): UnitStatus {
  return (status as UnitStatus) || 'available'
}
